import React from 'react';
import { Box, Button, IconButton } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import UnitPageShell from '../../components/shared/UnitPageShell';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import ZoomableImage from '../../components/shared/ZoomableImage';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { uploadUnitAsset, updateUnitContent, useUnitOverride } from '../../services/unitOverridesService';

const PAGE_ID = 'm1_u3_x3_rauchausloesende';

const M1_U3_X3_Rauchausloesende: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM1, canUploadImagesM1 } = useAuth();
  const canEditTexts = canEditTextsM1;
  const canUploadImages = canUploadImagesM1;

  // Overrides laden
  const { override, refresh } = useUnitOverride(PAGE_ID);

  // Basistexte
  const base = {
    intro:
      'Rauchauslösende Brandschutzklappen (RBSK) sind wichtige Elemente im vorbeugenden Brandschutz. Sie schließen im Falle einer Rauchentwicklung automatisch und verhindern so die Ausbreitung von Rauch und Brandgasen über die Lüftungskanäle in andere Bereiche des Gebäudes. Sie reagieren auf die Rauchentwicklung nicht auf die Wärme.',
    funktionsweise:
      'RBSK werden in der Regel über einen Rauchmelder ausgelöst, der entweder in die Klappe integriert ist oder als externer Melder in der Nähe der Klappe angebracht wird. Registriert der Rauchmelder Rauch, gibt er ein Signal an die Auslöseeinheit der RBSK. Daraufhin wird die Klappe unverzüglich geschlossen. Für die Schliessung gibt es unterschiedliche Lösungen.\n\n• Motorisch: Die Klappe schliesst mithilfe eines Motors.\n• Federrücklauf: Die Klappe wird durch eine Feder geschlossen.',
    einsatzgebiete:
      'RBSK werden überall dort eingesetzt, wo eine schnelle Reaktion auf Rauchentwicklung wichtig ist. Typische Einsatzgebiete sind:\n\n• Flucht- und Rettungswege: Um diese möglichst lange rauchfrei zu halten.\n• Große Gebäudekomplexe: Um eine schnelle Brandausbreitung zu verhindern.\n• Industrieanlagen: Zum Schutz von Personen und Anlagen.\n• Rechenzentren: Um Schäden durch Rauch zu minimieren.',
    anschlusstypen:
      'RBSK können auf unterschiedliche Weise an die Steuerung und die GLT angeschlossen werden:\n\n• 24V DC/AC: Weit verbreitet für den Betrieb der Rauchmelder und die Ansteuerung der Klappe.\n• 230V AC: Für leistungsstärkere Motoren.\n• Potenzialfreie Kontakte: Für Meldungen wie "Klappe offen", "Klappe geschlossen", "Störung".\n• Bus-Systeme (z.B. LON, BACnet, Modbus): Für die Einbindung in komplexe Gebäudeautomationssysteme.\n\nDie gewählte Anschlussart hängt von den Anforderungen des Gebäudes und der GLT ab. Die Ansteuerung kann direkt über die BMA erfolgen oder über eine zwischengeschaltete Steuerung.',
    meldungen:
      'RBSK verfügen über verschiedene Meldungen und Alarme, die für die Überwachung und Steuerung wichtig sind:\n\n• Rauchalarm: Das Signal des Rauchmelders\n• Klappe offen/geschlossen: Meldet den Zustand der Klappe.\n• Störung: Meldet eine Fehlfunktion des Melders oder der Klappe.',
    glt:
      'Die Anbindung der RBSK an die Gebäudeleittechnik (GLT) erfolgt über potentialfreie Kontakte. Dadurch können verschiedene Meldungen übertragen werden.\n\n• Rauchalarm: Das Signal des Rauchmelders, das die Schließung der Klappe auslöst.\n• Klappe geschlossen: Bestätigung, dass die Klappe nach Rauchauslösung geschlossen wurde.\n• Klappe offen: Gibt den Betriebszustand der Klappe wieder.\n• Störung: Meldet eine Fehlfunktion des Rauchmelders, des Antriebs oder der Klappe selbst.\n\nDie Überwachung der Kontakte in der GLT erfolgt auf Drahtbruch. Dies bedeutet, dass sowohl eine offene als auch eine geschlossene Klappe als aktiver Zustand erkannt werden können. Sollte es zu einem Drahtbruch kommen, wird dies als Störung gemeldet. So ist sichergestellt, dass bei einer Störung, die Lüftungsanlage ausser Betrieb geht.\n\nDie GLT kann auf diese Meldungen reagieren, Alarme auslösen, den Betriebszustand der Klappen anzeigen und die entsprechenden Lüftungsanlagen gemäß der Brandfallmatrix abschalten. So kann die Ausbreitung des Rauchs verhindert werden. Für den Fall, dass die GLT ausfällt, schliesst die RBSK auch ohne die GLT selbstständig. Die Schliessung wird dann durch den Rauchmelder ausgelöst.'
  };

  const blocks = React.useMemo(() => ([
    { id: 'block_intro', heading: undefined as any, body: override?.texts?.['block_intro'] ?? base.intro },
    { id: 'block_funktionsweise', heading: 'Funktionsweise', body: override?.texts?.['block_funktionsweise'] ?? base.funktionsweise },
    { id: 'block_einsatzgebiete', heading: 'Einsatzgebiete', body: override?.texts?.['block_einsatzgebiete'] ?? base.einsatzgebiete },
    { id: 'block_anschlusstypen', heading: 'Anschlusstypen', body: override?.texts?.['block_anschlusstypen'] ?? base.anschlusstypen },
    { id: 'block_meldungen', heading: 'Meldungen und Alarme', body: override?.texts?.['block_meldungen'] ?? base.meldungen },
    { id: 'block_glt', heading: 'Anbindung an die GLT und Überwachung', body: override?.texts?.['block_glt'] ?? base.glt }
  ]), [
    override,
    base.anschlusstypen,
    base.einsatzgebiete,
    base.funktionsweise,
    base.glt,
    base.intro,
    base.meldungen
  ]);

  // TTS
  const fullPlainText = React.useMemo(() => blocks.map(b => (b.heading ? `${b.heading}. ${b.body}` : b.body)).join(' '), [blocks]);
  const { speak, stop, speaking } = useTTS({ text: fullPlainText });

  // Admin: Bild ersetzen
  const handleImageReplace = React.useCallback(async (key: string, file: File) => {
    if (!token) return alert('Kein Token.');
    if (!canUploadImages) return alert('Keine Berechtigung fuer Bilder-Uploads.');
    try { await uploadUnitAsset(PAGE_ID, key, file, token); alert('Bild aktualisiert.'); refresh(); }
    catch (e: any) { alert('Upload fehlgeschlagen: ' + (e?.message || e)); }
  }, [token, refresh, canUploadImages]);

  // Admin: Text speichern
  const handleTextSave = React.useCallback(async (blockId: string, newBody: string) => {
    if (!token) return alert('Kein Token.');
    if (!canEditTexts) return alert('Keine Berechtigung zum Texte bearbeiten.');
    try { await updateUnitContent(PAGE_ID, { texts: { [blockId]: newBody } }, token); alert('Text gespeichert.'); refresh(); }
    catch (e: any) { alert('Speichern fehlgeschlagen: ' + (e?.message || e)); }
  }, [token, refresh, canEditTexts]);

  const ttsBtn = React.createElement(IconButton, { onClick: speaking ? stop : speak, 'aria-label': 'TTS', sx: { ml: 1 } }, React.createElement(SpeakerIcon, { sx: { fontSize: 28 } }));

  // Bildquellen mit Override-Fallback (unified pattern)
  const imgSrcFunc = (slot: string, fallback: string) => {
    const v = override?.images?.[slot];
    if (!v) return fallback;
    const s = String(v);
    return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`;
  };

  const imgSrc = {
    img_0: imgSrcFunc('img_0', '/assets/drawable/bsk_rae02.jpeg'),
    img_1: imgSrcFunc('img_1', '/assets/drawable/bsk_rae01.png'),
    img_2: imgSrcFunc('img_2', '/assets/drawable/bsk_rae05.jpeg'),
    img_3: imgSrcFunc('img_3', '/assets/drawable/bsk_rae04.jpeg'),
    img_4: imgSrcFunc('img_4', '/assets/drawable/bsk_rae03.jpeg')
  } as const;

  return (
    <UnitPageShell title="1.3.3 Rauch auslösende Brandschutzklappen" actions={ttsBtn}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
        {/* Linke Spalte */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <ZoomableImage
            src={imgSrc.img_0}
            alt="Rauch auslösende BSK"
            label="Bild einfügen"
            onReplace={canUploadImages ? (f) => handleImageReplace('img_0', f) : undefined}
          />

          <EditableTextBlock
            pageId={PAGE_ID}
            blockId="block_intro"
            body={blocks[0].body}
            onSave={(b) => handleTextSave('block_intro', b)}
          />

          <ZoomableImage
            src={imgSrc.img_1}
            alt="RBSK Detail 1"
            label="Bild einfügen"
            onReplace={canUploadImages ? (f) => handleImageReplace('img_1', f) : undefined}
          />

          <ZoomableImage
            src={imgSrc.img_2}
            alt="RBSK Detail 2"
            label="Bild einfügen"
            onReplace={canUploadImages ? (f) => handleImageReplace('img_2', f) : undefined}
          />
        </Box>

        {/* Rechte Spalte */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <EditableTextBlock
            pageId={PAGE_ID}
            blockId="block_funktionsweise"
            heading="Funktionsweise"
            body={blocks[1].body}
            onSave={(b) => handleTextSave('block_funktionsweise', b)}
          />

          <ZoomableImage
            src={imgSrc.img_3}
            alt="RBSK Funktion"
            label="Bild einfügen"
            onReplace={canUploadImages ? (f) => handleImageReplace('img_3', f) : undefined}
          />

          <EditableTextBlock
            pageId={PAGE_ID}
            blockId="block_einsatzgebiete"
            heading="Einsatzgebiete"
            body={blocks[2].body}
            onSave={(b) => handleTextSave('block_einsatzgebiete', b)}
          />

          <EditableTextBlock
            pageId={PAGE_ID}
            blockId="block_anschlusstypen"
            heading="Anschlusstypen"
            body={blocks[3].body}
            onSave={(b) => handleTextSave('block_anschlusstypen', b)}
          />

          <ZoomableImage
            src={imgSrc.img_4}
            alt="RBSK Anschluss"
            label="Bild einfügen"
            onReplace={canUploadImages ? (f) => handleImageReplace('img_4', f) : undefined}
          />

          <EditableTextBlock
            pageId={PAGE_ID}
            blockId="block_meldungen"
            heading="Meldungen und Alarme"
            body={blocks[4].body}
            onSave={(b) => handleTextSave('block_meldungen', b)}
          />

          <EditableTextBlock
            pageId={PAGE_ID}
            blockId="block_glt"
            heading="Anbindung an die GLT und Überwachung"
            body={blocks[5].body}
            onSave={(b) => handleTextSave('block_glt', b)}
          />

          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button variant="outlined" onClick={() => navigate('/units/m1_u3_x99_quiz')}>Zum Quiz</Button>
          </Box>
        </Box>
      </Box>
    </UnitPageShell>
  );
};

export default M1_U3_X3_Rauchausloesende;

